/**
 * Inline UI Components
 * 
 * Interactive UI components that can be embedded in chat messages to provide
 * rich, structured data visualization and interaction.
 */

/* eslint-disable react/prop-types */

import React from 'react';
import './InlineUI.css';

// --- Icon Components ---

const TrendUpIcon = (props) => (
  <svg {...props} fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const TrendDownIcon = (props) => (
  <svg {...props} fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
);

// --- Component: Project Details ---

export const ProjectDetailsView = ({content}) => {
  if (!content) {
    throw new Error('Invalid project details content: content is null or undefined');
  }

  return (
    <div className="inline-ui-container project-details">
      <div className="inline-ui-grid">
        <div className="inline-ui-row">
          <dt className="inline-ui-label">{'Project Owner'}</dt>
          <dd className="inline-ui-value">{content.projectOwner}</dd>
        </div>
        <div className="inline-ui-row">
          <dt className="inline-ui-label">{'Team Contact'}</dt>
          <dd className="inline-ui-value inline-ui-link">{content.teamContact}</dd>
        </div>
        <div className="inline-ui-row">
          <dt className="inline-ui-label">{'Default Branch'}</dt>
          <dd className="inline-ui-value">{content.defaultBranch}</dd>
        </div>
        <div className="inline-ui-row">
          <dt className="inline-ui-label">{'Repository URL'}</dt>
          <dd className="inline-ui-value inline-ui-link inline-ui-truncate">{content.repositoryURL}</dd>
        </div>
      </div>
      
      {content.environments && content.environments.length > 0 && (
        <>
          <h3 className="inline-ui-subtitle">{'Environments'}</h3>
          <div className="inline-ui-table-wrapper">
            <table className="inline-ui-table">
              <thead>
                <tr>
                  <th>{'Environment Label'}</th>
                  <th>{'Environment ID'}</th>
                  <th>{'Is Production'}</th>
                  <th>{'Hosts'}</th>
                </tr>
              </thead>
              <tbody>
                {content.environments.map((env, idx) => (
                  <tr key={idx}>
                    <td>{env.label}</td>
                    <td>{env.id}</td>
                    <td>{env.isProduction ? 'true' : 'false'}</td>
                    <td className="inline-ui-link">{env.hosts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      
      {content.footer && (
        <p className="inline-ui-footer">{content.footer}</p>
      )}
    </div>
  );
};

// --- Component: Metrics ---

export const MetricsView = ({content}) => {
  if (!content || !content.metrics) {
    throw new Error('Invalid metrics content: missing metrics array');
  }

  return (
    <div className="inline-ui-container metrics">
      {content.title && (
        <h3 className="inline-ui-title">{content.title}</h3>
      )}
      <div className="inline-ui-metrics-grid">
        {content.metrics.map((metric, idx) => (
          <div className="inline-ui-metric-card" key={idx}>
            <div className="inline-ui-metric-name">{metric.name}</div>
            <div className="inline-ui-metric-content">
              <div className="inline-ui-metric-value">{metric.value}</div>
              {metric.trend === 'up' && <TrendUpIcon className="inline-ui-trend-icon trend-up" />}
              {metric.trend === 'down' && <TrendDownIcon className="inline-ui-trend-icon trend-down" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Component: Diagram (Bar Chart) ---

export const DiagramView = ({content}) => {
  if (!content || !content.data) {
    throw new Error('Invalid diagram content: missing data array');
  }

  const maxValue = Math.max(...content.data.map(item => item.value));

  return (
    <div className="inline-ui-container diagram">
      {content.title && (
        <h3 className="inline-ui-title">{content.title}</h3>
      )}
      <div className="inline-ui-chart">
        <div className="inline-ui-chart-bars">
          {content.data.map((item, idx) => (
            <div className="inline-ui-chart-bar-wrapper" key={idx}>
              <div 
                className="inline-ui-chart-bar"
                style={{height: `${(item.value / maxValue) * 100}%`}}
                title={`${item.label}: ${item.value}`}
              />
              <div className="inline-ui-chart-label">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Component: Logs ---

export const LogView = ({content}) => {
  if (!content) {
    throw new Error('Invalid log content: content is null or undefined');
  }

  return (
    <div className="inline-ui-container log">
      {content.title && (
        <h3 className="inline-ui-log-title">{content.title}</h3>
      )}
      <pre className="inline-ui-log-content">{content.log ? content.log.trim() : ''}</pre>
    </div>
  );
};

// --- Component: Interactive Choices ---

export const ChoiceView = ({content, onChoice}) => {
  if (!content || !content.options) {
    throw new Error('Invalid choice content: missing options array');
  }

  return (
    <div className="inline-ui-container choice">
      {content.prompt && (
        <p className="inline-ui-choice-prompt">{content.prompt}</p>
      )}
      <div className="inline-ui-choice-buttons">
        {content.options.map((option, idx) => (
          <button
            className="inline-ui-choice-button"
            key={idx}
            onClick={() => onChoice && onChoice(option.text)}
          >
            {option.text}
          </button>
        ))}
      </div>
    </div>
  );
};

// --- Component: Task Progress ---

export const TaskProgressView = ({content}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  
  if (!content || !content.tasks) {
    throw new Error('Invalid task progress content: missing tasks array');
  }

  const completedCount = content.tasks.filter(task => task.status === 'completed').length;
  const totalCount = content.tasks.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="inline-ui-container task-progress">
      {/* Header with collapsible toggle */}
      <div 
        className="inline-ui-task-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{cursor: 'pointer'}}
      >
        <div className="inline-ui-task-header-left">
          <span className="inline-ui-task-toggle">
            {isExpanded ? '▼' : '▶'}
          </span>
          <span className="inline-ui-task-title">
            {content.title || 'Tasks'} {'('}{completedCount}{'/'}{totalCount}{')'}
          </span>
        </div>
        <div className="inline-ui-task-progress-bar-container">
          <div 
            className="inline-ui-task-progress-bar"
            style={{width: `${progressPercentage}%`}}
          />
        </div>
      </div>

      {/* Task List */}
      {isExpanded && (
        <div className="inline-ui-task-list">
          {content.tasks.map((task, idx) => (
            <div 
              className={`inline-ui-task-item status-${task.status}`}
              key={idx}
            >
              <div className="inline-ui-task-checkbox">
                {task.status === 'completed' && <span className="inline-ui-task-check">{'✓'}</span>}
                {task.status === 'in-progress' && <span className="inline-ui-task-spinner">{'⟳'}</span>}
                {task.status === 'not-started' && <span className="inline-ui-task-circle">{'○'}</span>}
              </div>
              <div className="inline-ui-task-content">
                <div className="inline-ui-task-name">{task.title}</div>
                {task.description && (
                  <div className="inline-ui-task-description">{task.description}</div>
                )}
              </div>
              <div className="inline-ui-task-status">
                {task.status === 'completed' && <span className="status-badge status-completed">{'Done'}</span>}
                {task.status === 'in-progress' && <span className="status-badge status-in-progress">{'In Progress'}</span>}
                {task.status === 'not-started' && <span className="status-badge status-not-started">{'Pending'}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer with summary message */}
      {content.summary && (
        <div className="inline-ui-task-footer">
          {content.summary}
        </div>
      )}
    </div>
  );
};

// --- Main Component Router ---

/**
 * Render the appropriate inline UI component based on content type
 */
export const InlineUIRenderer = ({type, content, onChoice, rawText}) => {
  const [renderError, setRenderError] = React.useState(null);

  // Error boundary effect - catch any rendering errors
  React.useEffect(() => {
    setRenderError(null);
  }, [type, content]);

  try {
    // Handle multiple structured blocks
    if (type === 'multiple' && Array.isArray(content)) {
      return (
        <>
          {content.map((block, idx) => (
            <InlineUIRenderer
              content={block.structuredContent}
              key={idx}
              onChoice={onChoice}
              rawText={rawText}
              type={block.contentType}
            />
          ))}
        </>
      );
    }
    
    // Handle single structured block
    let component = null;
    switch (type) {
      case 'project-details':
        component = <ProjectDetailsView content={content} />;
        break;
      case 'metrics':
        component = <MetricsView content={content} />;
        break;
      case 'diagram':
        component = <DiagramView content={content} />;
        break;
      case 'log':
        component = <LogView content={content} />;
        break;
      case 'choice':
        component = <ChoiceView content={content} onChoice={onChoice} />;
        break;
      case 'task-progress':
        // Task progress is shown in the right panel, not inline
        // Return null immediately without showing error
        return null;
      default:
        component = null;
    }

    // If component rendered successfully, return it
    if (component && !renderError) {
      return component;
    }

    // If no component or there was an error, show error message with raw content
    if (renderError || !component) {
      return (
        <div className="inline-ui-error">
          <div className="inline-ui-error-message">
            {'⚠️ Failed to render structured content'}
            {type && <span className="inline-ui-error-type">{` (${type})`}</span>}
          </div>
          {renderError && (
            <div className="inline-ui-error-details">
              {renderError.message}
            </div>
          )}
          {rawText && (
            <details className="inline-ui-error-raw">
              <summary>{'Show raw content'}</summary>
              <pre>{rawText}</pre>
            </details>
          )}
        </div>
      );
    }

    return component;
  } catch (error) {
    // Catch any synchronous rendering errors
    return (
      <div className="inline-ui-error">
        <div className="inline-ui-error-message">
          {'⚠️ Failed to render structured content'}
          {type && <span className="inline-ui-error-type">{` (${type})`}</span>}
        </div>
        <div className="inline-ui-error-details">
          {error.message}
        </div>
        {rawText && (
          <details className="inline-ui-error-raw">
            <summary>{'Show raw content'}</summary>
            <pre>{rawText}</pre>
          </details>
        )}
      </div>
    );
  }
};

